// Ticket expiration helper functions

// Application IDs to check for ticket expiration warnings
const DEV_APP_IDS = ["6195ee31ceeeaa90886f5187", "5f0d28497fbc36716ccae4d7"];

const PROD_APP_IDS = ["5f4cb4e09476351522ef8507"];

const VALID_APP_IDS = [...DEV_APP_IDS, ...PROD_APP_IDS];

/**
 * Check if a ticket is expiring within the specified number of days
 * @param {string} expiresAt - ISO date string
 * @param {number} daysThreshold - Number of days to check (default: 30)
 * @returns {boolean} - True if expiring within threshold
 */
export const isExpiringWithinDays = (expiresAt, daysThreshold = 30) => {
  if (!expiresAt) return false;

  const now = new Date();
  const expiryDate = new Date(expiresAt);
  const daysUntilExpiry = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));

  return daysUntilExpiry > 0 && daysUntilExpiry <= daysThreshold;
};

/**
 * Check if a ticket should show expiration warning
 * @param {Object} ticket - Ticket object
 * @returns {boolean} - True if should show warning
 */
export const shouldShowExpirationWarning = (ticket) => {
  if (!ticket) return false;

  const { status, expiresAt, application } = ticket;

  // Check if status is activated
  if (status !== "activated") return false;

  // Check if application ID is in the valid list
  if (!application || !VALID_APP_IDS.includes(application.id)) return false;

  // Check if expiring within 30 days (1 month)
  return isExpiringWithinDays(expiresAt, 30);
};

/**
 * Get days until expiry
 * @param {string} expiresAt - ISO date string
 * @returns {number} - Days until expiry
 */
export const getDaysUntilExpiry = (expiresAt) => {
  if (!expiresAt) return 0;

  const now = new Date();
  const expiryDate = new Date(expiresAt);
  const daysUntilExpiry = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));

  return Math.max(0, daysUntilExpiry);
};

/**
 * Filter tickets that need expiration warning, sorted most urgent first
 * @param {Array} tickets - Array of ticket objects
 * @returns {Array} - Array of expiring tickets (ascending by days left)
 */
export const getExpiringTickets = (tickets) => {
  if (!tickets || !Array.isArray(tickets)) return [];

  return tickets
    .filter(shouldShowExpirationWarning)
    .slice()
    .sort(
      (a, b) =>
        getDaysUntilExpiry(a.expiresAt) - getDaysUntilExpiry(b.expiresAt)
    );
};

// ---------------------------------------------------------------------------
// Urgency levels
// ---------------------------------------------------------------------------

export const EXPIRY_LEVELS = {
  CRITICAL: "critical", // <= 3 days
  WARNING: "warning", // <= 7 days
  NOTICE: "notice" // <= 30 days
};

const CRITICAL_DAYS = 3;
const WARNING_DAYS = 7;

// Colors keyed by level (used by toast, modal and navbar badge)
export const EXPIRY_LEVEL_COLORS = {
  [EXPIRY_LEVELS.CRITICAL]: {
    main: "#c62828",
    light: "#ffebee",
    text: "#ffffff"
  },
  [EXPIRY_LEVELS.WARNING]: {
    main: "#e65100",
    light: "#ffe8d6",
    text: "#ffffff"
  },
  [EXPIRY_LEVELS.NOTICE]: {
    main: "#f57f17",
    light: "#fff8e1",
    text: "#ffffff"
  }
};

/**
 * Get urgency level from days left
 * @param {number} daysLeft
 * @returns {string} - One of EXPIRY_LEVELS
 */
export const getExpiryLevel = (daysLeft) => {
  if (daysLeft <= CRITICAL_DAYS) return EXPIRY_LEVELS.CRITICAL;
  if (daysLeft <= WARNING_DAYS) return EXPIRY_LEVELS.WARNING;
  return EXPIRY_LEVELS.NOTICE;
};

/**
 * Get the most urgent level among the given tickets
 * @param {Array} tickets - Expiring tickets
 * @returns {string|null} - Level, or null when list is empty
 */
export const getMostUrgentLevel = (tickets) => {
  if (!tickets || tickets.length === 0) return null;

  const order = [
    EXPIRY_LEVELS.CRITICAL,
    EXPIRY_LEVELS.WARNING,
    EXPIRY_LEVELS.NOTICE
  ];
  let best = order.length;

  tickets.forEach((ticket) => {
    const idx = order.indexOf(
      getExpiryLevel(getDaysUntilExpiry(ticket.expiresAt))
    );
    if (idx !== -1 && idx < best) best = idx;
  });

  return best < order.length ? order[best] : null;
};

// ---------------------------------------------------------------------------
// Toast "show once" policy
// ---------------------------------------------------------------------------

const TOAST_DISMISSED_KEY = "ticketExpiryToastDismissed";

const getLocalDateKey = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const getTicketKey = (ticket, index) =>
  ticket.id || ticket._id || `${ticket.name || "ticket"}-${index}`;

/**
 * Build a stable signature for a set of expiring tickets.
 * Changes when: the day changes, a ticket is added/removed,
 * or any ticket crosses into a more urgent level.
 * @param {Array} tickets
 * @returns {string}
 */
export const getToastSignature = (tickets) => {
  if (!tickets || tickets.length === 0) return "";

  const parts = tickets
    .map(
      (ticket, index) =>
        `${getTicketKey(ticket, index)}:${getExpiryLevel(
          getDaysUntilExpiry(ticket.expiresAt)
        )}`
    )
    .sort();

  return `${getLocalDateKey()}|${parts.join(",")}`;
};

/**
 * Whether the toast should pop for these tickets
 * (true unless the exact same signature was already dismissed).
 * @param {Array} tickets
 * @returns {boolean}
 */
export const shouldShowExpiryToast = (tickets) => {
  if (!tickets || tickets.length === 0) return false;

  const signature = getToastSignature(tickets);
  try {
    return localStorage.getItem(TOAST_DISMISSED_KEY) !== signature;
  } catch (e) {
    // localStorage unavailable (private mode, quota) → always show
    return true;
  }
};

/**
 * Remember that the toast for these tickets was dismissed
 * @param {Array} tickets
 */
export const markExpiryToastDismissed = (tickets) => {
  if (!tickets || tickets.length === 0) return;

  try {
    localStorage.setItem(TOAST_DISMISSED_KEY, getToastSignature(tickets));
  } catch (e) {
    // Ignore storage errors — worst case the toast shows again next load
  }
};
